/**
 *
 * FlexPopup
 *
 */

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import ReactDOM from 'react-dom';
// import PropTypes from 'prop-types';
// import styled from 'styled-components';

import { FormattedMessage } from 'react-intl';
import messages from './messages';
import styles from './styles.module.css';

import { Icon } from 'semantic-ui-react';

import FlexBox from 'components/common/FlexBox';
import { isMobileOnly } from 'react-device-detect';
import FlexButton from 'components/common/FlexButton';

function getScrollParent(node) {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode);
  }
}
const FlexPopup = forwardRef((props, ref) => {
  const {
    content,
    contentMatchWidth,
    style,
    fill,
    position = 'top middle',
    closable,
    type = 'info',
    closeOnBlur,
    title,
    description,
    transparent,
  } = props;
  const [open, setOpen] = useState(props.defaultOpen ? props.defaultOpen : false);
  const [showContent, setShowContent] = useState(false);
  const popupContainerRef = useRef(null);
  let menuRef = useRef(null);
  if (ref) {
    menuRef = ref;
  }
  const [stackIndex, setStackIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const localData = useRef({
    open: props.defaultOpen ? props.defaultOpen : false,
    clicked: props.defaultOpen ? props.defaultOpen : false,
    openedOccurred: props.defaultOpen ? props.defaultOpen : false,
  });

  let elemStyle = style ? { ...style } : {};

  if (fill) {
    elemStyle.height = '100%';
    elemStyle.width = '100%';
  }

  let classes = `${styles.flexPopupContainer}`;
  if (props.className) {
    classes = ` ${props.className} ` + classes;
  }

  useEffect(() => {
    let clickListener = (e) => {
      if (
        closeOnBlur !== false &&
        localData.current.open &&
        popupContainerRef.current &&
        popupContainerRef.current !== e.target &&
        !popupContainerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        closePopup(e);
      }
    };

    let resizeListener = (e) => {
      forceUpdate();
    };

    let scrollListener = (e) => {
      if (props.movesWithScrollEvents) {
        forceUpdate();
      }
    };

    window.addEventListener('click', clickListener);
    window.addEventListener('resize', resizeListener);
    window.addEventListener('scroll', scrollListener, true);
    return () => {
      window.removeEventListener('click', clickListener);
      window.removeEventListener('resize', resizeListener);
      window.removeEventListener('scroll', scrollListener);
    };
  }, []);

  const closePopup = (e) => {
    setOpen(false);
    setShowContent(false);
    localData.current.open = false;
    localData.current.clicked = false;
    clearTimeout(localData.current.closeMenuTimeout);
    localData.current.closeMenuTimeout = undefined;
    e.stopPropagation();

    if (props.onClose) {
      props.onClose(e);
    }
  };

  const handleOnClick = (e) => {
    if (isMobileOnly || props.clickable) {
      if (props.propagation === false) {
        e.stopPropagation();
      }
      if (e.target !== menuRef.current && (!menuRef.current || !menuRef.current.contains(e.target))) {
        setOpen(!open);
        setShowContent(!open);
        localData.current.open = !localData.current.open;
        localData.current.clicked = true;
        if (localData.current.open) {
          // Ensures that the close animation does not occur on initialization of the component
          localData.current.openedOccurred = true;
          if (!window._globaPopupStackIndex) {
            window._globaPopupStackIndex = 1;
          } else {
            window._globaPopupStackIndex++;
          }

          setStackIndex(window._globaPopupStackIndex);
        }

        if (!open && !props.open) {
          if (props.onOpen) {
            props.onOpen();
          }
        } else {
          if (props.onClose) {
            props.onClose();
          }
        }
        forceUpdate();
      } else {
        if (
          closeOnBlur !== false &&
          localData.current.clicked &&
          !popupContainerRef.current.contains(e.target) &&
          (!menuRef.current || !menuRef.current.contains(e.target))
        ) {
          closePopup(e);
        }
      }
    }
  };

  const handlePointerEnter = (e) => {
    if (!isMobileOnly && props.hoverable !== false && !props.clickable && props.open !== false) {
      if (popupContainerRef.current.contains(e.target) || menuRef.current.contains(e.target)) {
        popupContainerRef.current.onMouseLeave = null;
        clearTimeout(localData.current.closeMenuTimeout);
        localData.current.closeMenuTimeout = undefined;

        setOpen(true);
        setShowContent(true);
        localData.current.open = true;
        localData.current.openedOccurred = true;
        forceUpdate();

        if (!window._globaPopupStackIndex) {
          window._globaPopupStackIndex = 1;
        } else {
          window._globaPopupStackIndex++;
        }

        if (props.onOpen) {
          props.onOpen();
        }

        setStackIndex(window._globaPopupStackIndex);
      }
    }
  };

  const handleContentPointerEnter = (e) => {
    handlePointerEnter(e);
  };

  const handlePointerLeave = (e) => {
    if (!isMobileOnly && !localData.current.clicked) {
      localData.current.closeMenuTimeout = setTimeout(
        () => {
          localData.current.open = false;
          setOpen(false);
        },
        type === 'tooltip' || type === 'warn-tooltip' ? 250 : 250,
      );

      localData.current.openMenuDeferred = undefined;
      setShowContent(false);
    }
  };

  let contentElem;
  let menuStyle = {};
  let positionTokens = position.split(' ');
  let needsRecalc = false;
  if (popupContainerRef.current) {
    let bounds = popupContainerRef.current.getBoundingClientRect();
    let menuBounds = menuRef.current
      ? menuRef.current.getBoundingClientRect()
      : {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: 0,
          height: 0,
        };
    if (positionTokens[0] === 'top') {
      menuStyle.top = bounds.top;
      menuStyle.left = bounds.left + bounds.width / 2 - menuBounds.width / 2;
    } else if (positionTokens[0] === 'bottom') {
      menuStyle.top = bounds.top + bounds.height;
      menuStyle.left = bounds.left + bounds.width / 2 - menuBounds.width / 2;
    } else if (positionTokens[0] === 'right') {
      menuStyle.top = bounds.top + bounds.height / 2 - menuBounds.height / 2;
      menuStyle.left = bounds.right;
    } else if (positionTokens[0] === 'left') {
      menuStyle.top = bounds.top + bounds.height / 2 - menuBounds.height / 2;
      menuStyle.right = bounds.right - bounds.width - menuBounds.width;
    } else if (positionTokens[0] === 'center') {
      menuStyle.left = bounds.left + bounds.width / 2 - menuBounds.width / 2;
      menuStyle.top = bounds.top + bounds.height / 2 - menuBounds.height / 2;
    }

    if (menuRef.current) {
      if (menuStyle.left + menuBounds.width > window.innerWidth) {
        menuStyle.left = window.innerWidth - menuBounds.width + 10;
      }
      if (menuStyle.top + menuBounds.height > window.innerHeight) {
        menuStyle.top = bounds.top - menuBounds.height - 10;
      }
      if (menuStyle.top < 10) {
        menuStyle.top = 10;
      }
      if (menuStyle.left < 10) {
        menuStyle.left = -10;
      }
      if (contentMatchWidth) {
        menuStyle.width = bounds.width;
      }
      // menuStyle.top -= menuBounds.height + 5;
      // menuStyle.left = Math.min(bounds.left, window.innerWidth - menuBounds.width - 10);
      // menuStyle.top = Math.min(window.innerHeight - menuBounds.height - 10, bounds.top + bounds.height);
    }

    if (props.movesWithScrollEvents) {
      if (!popupContainerRef.current.scrollParent) {
        popupContainerRef.current.scrollParent = getScrollParent(popupContainerRef.current);
      }

      if (popupContainerRef.current.scrollParent) {
        let scrollParentBounds = popupContainerRef.current.scrollParent.getBoundingClientRect();
        if (bounds.top < scrollParentBounds.top) {
          menuStyle.opacity = 0.0;
        }
      } else if (bounds.top < 0 || bounds.top > window.innerHeight) {
        menuStyle.opacity = 0.0;
      }
    }

    if (menuBounds.width === 0 && menuBounds.height === 0) {
      needsRecalc = true;
    }
  }

  if ((!open && !props.open) || props.open === false) {
    menuStyle.display = 'none';
  } else {
    menuStyle.zIndex = stackIndex;
    if (needsRecalc) {
      menuStyle.opacity = 0.0;
      setTimeout(() => {
        forceUpdate();
      }, 0);
    }
  }

  if (props.width) {
    menuStyle.width = props.width;
  }

  let contentClone = content;
  if (contentClone && contentClone.type !== 'div' && contentClone.type !== 'a' && typeof contentClone !== 'string') {
    contentClone = React.cloneElement(contentClone, {
      closePopup: closePopup,
      ...contentClone.props,
    });
  }

  let contentClasses = `${styles.popupContentContainer} ${styles[positionTokens[0]]} ${positionTokens.length > 1 && styles[positionTokens[1]]} ${showContent || props.open ? styles.showContent : styles.hideContent}`;
  if (type === 'info') {
    contentClasses = contentClasses + ` ${styles.info}`;
  }
  if (type === 'tooltip') {
    contentClasses = contentClasses + ` ${styles.tooltip}`;
  }
  if (type === 'warn-tooltip') {
    contentClasses = contentClasses + ` ${styles.tooltip} ${styles.warn}`;
  }
  if (transparent) {
    contentClasses = contentClasses + ` ${styles.transparent}`;
  }
  if (props.constrain || props.constrained) {
    contentClasses = contentClasses + ` ${styles.constrain}`;
  }
  if (props.contentClassName) {
    contentClasses = contentClasses + ` ${props.contentClassName}`;
  }

  if (needsRecalc) {
    contentClasses += ` ${styles.calculating}`;
  }

  contentElem = (
    <div
      ref={menuRef}
      style={menuStyle}
      onMouseEnter={handleContentPointerEnter}
      onMouseLeave={handlePointerLeave}
      className={contentClasses}
    >
      {(title || description) && (
        <div className={styles.popupHeader}>
          {title && <div className={styles.popupTitle}>{title}</div>}
          {description && <div className={styles.popupDescription}>{description}</div>}
        </div>
      )}
      {contentClone}
      {closable && (
        <FlexButton
          topGutter
          onClick={(e) => {
            e.stopPropagation();
            closePopup(e, true);
          }}
        >
          Close
        </FlexButton>
      )}
    </div>
  );

  let elementClone = contentElem;
  if (elementClone && elementClone.type !== 'div' && elementClone.type !== 'a') {
    elementClone = React.cloneElement(contentElem, {
      closePopup: closePopup,
      ...contentElem.props,
    });
  }

  return (
    <div
      ref={popupContainerRef}
      className={classes}
      onClick={handleOnClick}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
      style={elemStyle}
    >
      {React.Children.map(props.children, (child) => {
        if (child.type === 'div' || child.type === 'a') {
          return child;
        } else {
          return React.cloneElement(child, {
            closePopup: closePopup,
            ...child.props,
          });
        }
      })}
      {/* {props.children} */}
      {document.getElementById('drag_drop_overlay') &&
        content &&
        ReactDOM.createPortal(React.cloneElement(elementClone), document.getElementById('drag_drop_overlay'))}
    </div>
  );
});

FlexPopup.propTypes = {};

export default FlexPopup;
